// projectlab-web/src/components/layout/PublicLayout.tsx
import { Outlet, Link, useLocation } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { Menu, X, ChevronRight } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import clsx from 'clsx'
import { Button } from '@/components/ui'
import { useAuth } from '@/hooks/useAuth'

const CHROMELESS_PATHS = new Set(['/', '/login', '/register', '/owner-register', '/auth/confirm'])

const NAV_LINKS = [
  { label: 'Explore Projects', href: '/talent/explore' },
  { label: 'About', href: '/about' },
]

function Logo({ size = 28 }: { size?: number }) {
  return (
    <Link to="/" className="group flex items-center gap-2.5" aria-label="ProjectLab home">
      <svg viewBox="0 0 32 32" width={size} height={size} fill="none" aria-hidden>
        <rect x="2" y="2" width="12" height="12" rx="2.5" fill="#1E40AF" className="transition-opacity group-hover:opacity-90" />
        <rect x="18" y="2" width="12" height="12" rx="2.5" fill="#3B82F6" opacity="0.82" />
        <rect x="2" y="18" width="12" height="12" rx="2.5" fill="#3B82F6" opacity="0.82" />
        <rect x="18" y="18" width="12" height="12" rx="2.5" fill="#1E40AF" opacity="0.52" />
      </svg>
      <span className="font-display text-base font-bold tracking-tight text-[var(--color-text)]">
        Project<span className="text-[var(--color-primary)]">Lab</span>
      </span>
    </Link>
  )
}

export function PublicLayout() {
  const { user, role } = useAuth()
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  const isChromeless = CHROMELESS_PATHS.has(location.pathname)

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 16)
    window.addEventListener('scroll', handler, { passive: true })
    return () => window.removeEventListener('scroll', handler)
  }, [])

  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname])

  const dashboardHref =
    role === 'owner' ? '/owner' : role === 'admin' ? '/admin' : '/talent'

  return (
    <div className="flex min-h-dvh flex-col bg-[var(--color-bg)] text-[var(--color-text)]">
      <a href="#main-content" className="skip-link">
        Skip to content
      </a>

      {!isChromeless && (
        <header
          className={clsx(
            'fixed inset-x-0 top-0 z-40 transition-all duration-300',
            scrolled
              ? 'border-b border-[var(--color-border)] bg-white/95 shadow-sm backdrop-blur-xl'
              : 'bg-white/90 backdrop-blur-lg',
          )}
        >
          <div className="mx-auto flex h-16 max-w-[1200px] items-center justify-between gap-4 px-4 sm:px-6">
            <Logo />

            <nav aria-label="Main navigation" className="hidden items-center gap-1 md:flex">
              {NAV_LINKS.map((link) => {
                const active = location.pathname === link.href
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    className={clsx(
                      'rounded-xl px-3.5 py-2 text-sm font-medium transition-colors',
                      active
                        ? 'bg-[var(--color-surface-offset)] text-[var(--color-text)]'
                        : 'text-[var(--color-text-muted)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]',
                    )}
                  >
                    {link.label}
                  </Link>
                )
              })}
            </nav>

            <div className="hidden items-center gap-2 md:flex">
              {user ? (
                <Link to={dashboardHref}>
                  <Button variant="primary" size="sm" iconRight={<ChevronRight size={14} />}>
                    Dashboard
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" size="sm">
                      Log in
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button variant="primary" size="sm">
                      Join as Talent
                    </Button>
                  </Link>
                </>
              )}
            </div>

            <div className="flex items-center md:hidden">
              <button
                onClick={() => setMenuOpen((v) => !v)}
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
                className="flex h-10 w-10 items-center justify-center rounded-xl border border-[var(--color-border)] bg-white text-[var(--color-text-muted)] transition-all hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
              >
                {menuOpen ? <X size={18} /> : <Menu size={18} />}
              </button>
            </div>
          </div>

          <AnimatePresence>
            {menuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                className="overflow-hidden border-t border-[var(--color-border)] bg-white md:hidden"
              >
                <nav className="flex flex-col gap-1 p-4" aria-label="Mobile navigation">
                  {NAV_LINKS.map((link) => {
                    const active = location.pathname === link.href
                    return (
                      <Link
                        key={link.href}
                        to={link.href}
                        className={clsx(
                          'rounded-xl px-4 py-3 text-sm font-medium transition-colors',
                          active
                            ? 'bg-[var(--color-surface-offset)] text-[var(--color-text)]'
                            : 'text-[var(--color-text-muted)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]',
                        )}
                      >
                        {link.label}
                      </Link>
                    )
                  })}
                  <hr className="my-2 border-[var(--color-divider)]" />
                  {user ? (
                    <Link to={dashboardHref}>
                      <Button variant="primary" size="sm" fullWidth>
                        Dashboard
                      </Button>
                    </Link>
                  ) : (
                    <>
                      <Link to="/login">
                        <Button variant="ghost" size="sm" fullWidth>
                          Log in
                        </Button>
                      </Link>
                      <Link to="/register">
                        <Button variant="primary" size="sm" fullWidth>
                          Join as Talent
                        </Button>
                      </Link>
                    </>
                  )}
                </nav>
              </motion.div>
            )}
          </AnimatePresence>
        </header>
      )}

      <main id="main-content" className={isChromeless ? 'flex-1' : 'flex-1 pt-16'}>
        <Outlet />
      </main>

      {!isChromeless && (
        <footer className="mt-auto border-t border-[var(--color-border)] bg-[var(--color-surface)]">
          <div className="mx-auto max-w-[1200px] px-4 py-10 sm:px-6">
            <div className="mb-8 grid grid-cols-2 gap-8 md:grid-cols-4">
              <div className="col-span-2 md:col-span-1">
                <Logo size={24} />
                <p className="mt-3 max-w-[220px] text-xs leading-relaxed text-[var(--color-text-muted)]">
                  Hong Kong&apos;s verified project-based freelance platform.
                </p>
              </div>

              {[
                {
                  heading: 'Platform',
                  links: [
                    { label: 'Explore Projects', href: '/talent/explore' },
                    { label: 'About', href: '/about' },
                    { label: 'Join as Talent', href: '/register' },
                  ],
                },
                {
                  heading: 'Businesses',
                  links: [
                    { label: 'Register Business', href: '/owner-register' },
                    { label: 'Log in', href: '/login' },
                  ],
                },
                {
                  heading: 'Legal',
                  links: [
                    { label: 'Terms of Service', href: '/terms' },
                    { label: 'Privacy Policy', href: '/privacy' },
                    { label: 'Talent Protection', href: '/protection' },
                  ],
                },
              ].map((col) => (
                <div key={col.heading}>
                  <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                    {col.heading}
                  </h3>
                  <ul className="flex flex-col gap-2">
                    {col.links.map((l) => (
                      <li key={l.href}>
                        <Link
                          to={l.href}
                          className="text-sm text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-text)]"
                        >
                          {l.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="flex flex-col items-center justify-between gap-3 border-t border-[var(--color-divider)] pt-6 sm:flex-row">
              <p className="text-xs text-[var(--color-text-faint)]">
                {new Date().getFullYear()} ProjectLab. All rights reserved. Hong Kong
              </p>
              <p className="text-xs text-[var(--color-text-faint)]">
                Verified HK businesses only · Talent protected
              </p>
            </div>
          </div>
        </footer>
      )}
    </div>
  )
}